import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { PulseLoader } from 'react-spinners';

import { useModal } from '../context/ModalContext';
import Text from '../components/common/Text';
import LoadingSpinner from '../components/common/LoadingSpinner';
import HotelLogo from '../components/common/HotelLogo';
import SectionHeader from '../components/common/SectionHeader';
import AllInvoices from '../components/invoice/AllInvoices';
import Button from '../components/common/Button';
import {
  changeInvoiceAddress,
  checkout,
  getFolioInvoicePreview,
  getInvoicesByReservationId,
} from '../redux/reducer/actions';
import BillingAddressFormContainer from '../forms/billing-address-forms/BillingAddressFormContainer';
import removeCancellationInvoices from '../utils/removeCancellationInvoices';

const InvoicesPage = () => {
  const { uniqueId, propertyId } = useParams();
  const { openModal, closeModal } = useModal();
  const [isFullyPaid, setIsFullyPaid] = useState(false);
  const reservation = useSelector((state) => state.app.reservation).data;
  const invoices = useSelector((state) => state.app.invoices).list;
  const invoicesList = useMemo(() => invoices || [], [invoices]);
  const currentFolioIds = useSelector(
    (state) => state.app.currentFolioIds,
  ).data;
  const previewList = useSelector((state) => state.app.invoices).previewList;
  const { checkoutSuccessPageUrl, yourBillPageUrl } = useSelector(
    (state) => state.app.urls,
  );
  const [editInvoiceAddressMode, setEditInvoiceAddressMode] = useState(false);
  const [initialValues, setInitialValues] = useState();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Filter invoices to remove duplicates created when a user edits invoice address
  // console.log({ invoicesList });
  // console.log({ invoicesData });
  const filteredInvoicesList = removeCancellationInvoices(invoicesList);
  // Check the order in which the invoices are rendered
  // console.log(filteredInvoicesList.map((invoice) => invoice.folioId).join(', '));
  const filteredInvoicesData = previewList.length > 0 ? filteredInvoicesList.map((invoice) => previewList.find((i) => i.folioId === invoice.folioId)) : [];
  // console.log({ filteredInvoicesList });
  // console.log({ filteredInvoicesData });

  const [selectedInvoiceFolioId, setSelectedInvoiceFolioId] = useState(() => {
    if (filteredInvoicesList.length > 0) {
      return filteredInvoicesList[0].folioId;
    }
    return '';
  });

  const selectedInvoice = filteredInvoicesData.find((invoice) => invoice?.folioId === selectedInvoiceFolioId);
  // console.log(JSON.stringify(selectedInvoice));

  useEffect(() => {
    if (reservation) {
      dispatch(
        getInvoicesByReservationId({
          hotelId: uniqueId,
          reservationId: reservation.id,
        }),
      );
      if (reservation.balance.amount === 0) {
        setIsFullyPaid(true);
      }
    }
  }, [dispatch, reservation, uniqueId]);

  useEffect(() => {
    if (invoicesList?.length > 0) {
      const folioIds = invoicesList.map((invoice) => invoice.folioId);
      dispatch(getFolioInvoicePreview({ hotelId: uniqueId, folioIds }));
    }
  }, [currentFolioIds, dispatch, invoicesList, uniqueId]);

  useEffect(() => {
    if (previewList?.length > 0) {
      const nameParts = selectedInvoice?.to?.name?.trim().split(/\s+/);
      if (nameParts) {
        const firstName = nameParts[0] || '';
        const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
        const middleName = nameParts.slice(1, -1).join(' ') || '';

        const customer = {
          firstName,
          lastName,
          middleName,
          ...selectedInvoice.to.address,
        };
        setInitialValues(customer);
      }
    }
  }, [previewList, selectedInvoiceFolioId, selectedInvoice?.to?.address, selectedInvoice?.to?.name]);

  const onCancel = () => {
    setEditInvoiceAddressMode(false);
  };

  const onSubmit = async (values) => {
    let response;
    if (selectedInvoice) {
      response = await dispatch(
        changeInvoiceAddress({
          hotelId: uniqueId,
          propertyId,
          folioId: selectedInvoice.folioId,
          values,
        }),
      );
    }

    if (response.type.includes('rejected')) {
      NotificationManager.error(
        'Failed to update billing address',
        'Notification',
        3000,
      );
    } else {
      NotificationManager.success(
        'Success! Your billing address has been updated.',
        'Notification',
        3000,
      );

      await dispatch(getFolioInvoicePreview({ hotelId: uniqueId, selectedInvoiceFolioId }));
      await dispatch(
        getInvoicesByReservationId({
          hotelId: uniqueId,
          reservationId: reservation.id,
        }),
      );
      setEditInvoiceAddressMode(false);
    }
  };

  const checkoutInProgressModal = {
    title: 'Checkout in progress',
    body: (
      <div className="flex flex-col gap-8 py-2">
        <Text>Please do not refresh this page.</Text>
        <div className="mx-auto py-2">
          <PulseLoader
            color="var(--color-pj-primary-300)"
            loading
            size={14}
            speedMultiplier={0.6}
          />
        </div>
      </div>
    ),
  };

  const checkoutConfirmationModal = {
    title: 'Ready to leave the room?',
    body: (
      <Text>
        Please continue with your checkout only if you are leaving the room.
      </Text>
    ),
    primaryActionLabel: 'Confirm',
    primaryActionCallback: async () => {
      closeModal();
      openModal(checkoutInProgressModal);

      const response = await dispatch(
        checkout({ hotelId: uniqueId, reservationId: reservation.id }),
      );
      if (response && response.type.includes('fulfilled')) {
        navigate(checkoutSuccessPageUrl);
      } else {
        NotificationManager.error(
          'Checkout failed! please check with Reception',
          'Notification',
          3000,
        );
      }
      closeModal();
    },
    secondaryActionLabel: 'Cancel',
    secondaryActionCallback: closeModal,
  };

  return (
    <main className="flex min-h-screen flex-col gap-16 py-16">
      <HotelLogo />
      <section className="flex flex-col gap-6">
        {editInvoiceAddressMode ? (
          <SectionHeader text="Edit Invoice Address" />
        ) : (
          <SectionHeader text="Payment successful" />
        )}
        {!editInvoiceAddressMode && (
          <>
            <Text>
              Thank you for using payjim. Please check the invoice address below
              carefully. If you are ready to proceed, checkout now. Else, please
              adjust your invoice.
            </Text>
            <div className="flex flex-col gap-4 sm:flex-row">
              {isFullyPaid ? (
                <Button
                  variant="primary"
                  text="Checkout now"
                  type="button"
                  onClick={() => openModal(checkoutConfirmationModal)}
                />
              ) : (
                <Button
                  variant="primary"
                  text="Pay for remaining items"
                  type="button"
                  onClick={() => {
                    navigate(yourBillPageUrl);
                  }}
                />
              )}
            </div>
          </>
        )}

        {editInvoiceAddressMode && (
          <BillingAddressFormContainer
            initialValues={initialValues}
            onSubmit={onSubmit}
            cancelable
            onCancel={onCancel}
          />
        )}

        {!editInvoiceAddressMode
          && (invoicesList?.length === 0 || previewList?.length === 0) && (
            <LoadingSpinner message="Loading invoices" />
        )}

        {!editInvoiceAddressMode
          && invoicesList?.length > 0
          && previewList?.length > 0
          && filteredInvoicesList.length > 0 && (
            <AllInvoices
              filteredInvoicesList={filteredInvoicesList}
              filteredInvoicesData={filteredInvoicesData}
              selectedInvoiceFolioId={selectedInvoiceFolioId}
              setSelectedInvoiceFolioId={setSelectedInvoiceFolioId}
              selectedInvoice={selectedInvoice}
              setEditInvoiceAddressMode={setEditInvoiceAddressMode}
            />
        )}
      </section>
    </main>
  );
};

export default InvoicesPage;
